import { isDev, isLocal } from "./env";
import { Quote, QuoteError } from "../types/quote";
import { Amount } from "../types/amount";
import { OrderError } from "../types/order";

export const UI_ERROR_MESSAGES = {
  A: `Something went wrong. Please try again shortly.`,
  B: `There is insufficient liquidity for the trade at this time. Please reduce the trade size or try again later.`,
  C: `We are not able to provide a price at this time. Please try again later.`,
  D: `It took you too long to confirm the trade in your wallet. Please request a new quote.`,
  E: `There is an insufficient balance in your wallet to complete this trade.`,
  F: `Please increase your order size to cover the network fee at a minimum.`,
  H: `Order is too large. Please reduce the trade size or try again later.`,
  I: `Price moved too much and order cannot be accepted. Please request a new price.`,
  J: `All of our transaction workers are busy at the moment, please try again soon.`,
  K: `You seem to have set your token approvals on Bebop to custom amounts. Please increase the approvals on Etherscan for this trade to succeed.`,
} as const;
export const QUOTE_ERROR_CODES_MAPPING = {
  101: "A",
  102: "B",
  103: "A",
  104: "F",
  105: "A",
  106: "A",
  // HTTP - Status codes
  400: "A",
  403: "A",
  404: "A",
  429: "A",
  500: "A",
  501: "A",
  522: "A",
} as const;

export const ORDER_ERROR_CODE_MAPPING = {
  201: "A",
  202: "I",
  203: "D",
  204: "E",
  205: "K",
  206: "B",
  207: "A",
  208: "A",
  209: "J",
  210: "A",
  // HTTP - Status codes
  400: "A",
  403: "A",
  404: "A",
  429: "A",
  500: "A",
  501: "A",
  522: "A",
} as const;

type QuoteErrorCode = keyof typeof QUOTE_ERROR_CODES_MAPPING;
type OrderErrorCode = keyof typeof ORDER_ERROR_CODE_MAPPING;

const UNEXPECTED_ERROR =
  "There was an unexpected error. Please try again later. If the error persists contact a member of our team";

export const getErrorMessageFromQuoteError = (quoteError?: QuoteError): string | undefined => {
  if (quoteError?.error) {
    const errorCode = quoteError.error.errorCode;
    if (isValidQuoteErrorCode(errorCode)) {
      const uiErrorCodeMapping = QUOTE_ERROR_CODES_MAPPING[errorCode];
      // Special case for error code 106 - the error contains fee in usd and ether.
      if (errorCode === 106) {
        if (quoteError.error.fee?.usd) {
          return ` Please increase the order size to cover the network fee at a minimum. The current network fee is $${quoteError.error.fee.usd}.`;
        }
      }
      return UI_ERROR_MESSAGES[uiErrorCodeMapping];
    } else if (isLocal || isDev) {
      return `Dev Only Error. [${quoteError.error.errorCode}] ${quoteError.error.message}`;
    }
    return UNEXPECTED_ERROR;
  }
  return undefined;
};

export const getErrorMessageFromOrderError = (orderError: OrderError): string => {
  if (orderError?.error) {
    const errorCode = orderError.error.errorCode;
    if (isValidOrderError(errorCode)) {
      const uiErrorCodeMapping = ORDER_ERROR_CODE_MAPPING[errorCode];
      return UI_ERROR_MESSAGES[uiErrorCodeMapping];
    } else if (isLocal || isDev) {
      return `Dev Only Error. [${orderError.error.errorCode}] ${orderError.error.message}`;
    }
  }
  return UNEXPECTED_ERROR;
};

export const getQuoteWarnings = (quote: Quote | undefined): string | undefined => {
  if (!quote) {
    return undefined;
  }
  const networkFee = quote.usdGasFee;
  const totalAmountOut = Object.values(quote.takerTokens)
    .map(entry => entry.amountUsd)
    .reduce((acc, currentValue) => acc.add(currentValue), new Amount("0"));
  const gasPercentageOfTradeAmount = networkFee.value.div(totalAmountOut.value).mul(100);
  if (gasPercentageOfTradeAmount.gt(10)) {
    return `The current network fee is ${gasPercentageOfTradeAmount
      .round()
      .toString()}% of this trade. This fee is always included in the Bebop price.`;
  }
  return "";
};

const isValidQuoteErrorCode = (maybeErrorCode: number): maybeErrorCode is QuoteErrorCode => {
  return Object.keys(QUOTE_ERROR_CODES_MAPPING).includes(maybeErrorCode.toString());
};
const isValidOrderError = (maybeErrorCode: number): maybeErrorCode is OrderErrorCode => {
  return Object.keys(ORDER_ERROR_CODE_MAPPING).includes(maybeErrorCode.toString());
};
